import { CHART_TYPE, CommonChartModel } from './chart.model';
import Highcharts from 'highcharts';
import { exportConfig, TITLE_TYPE } from './dynamic-data-table.model';
import { dynamicComponentSchema, DynamicFormArray, SECTION_TYPE, TabConfig } from './form.model';
import { DashboardContainerComponent } from '../layout/dashboard-container/dashboard-container.component';
import { ApiEnvNames, FETCH_METHODS, SearchConfig } from './api.model';
import { SurveyAnswerAccordionComponent } from '../components/survey/survey-answer-accordion/survey-answer-accordion.component';
import { SurveyAttendeeTableComponent } from '../components/survey/survey-attendee-table/survey-attendee-table.component';
import { SurveyChartDrilldownComponent } from '../components/survey/survey-chart-drilldown/survey-chart-drilldown.component';
import { ChartComponent } from '../layout/chart/chart.component';
import { DynamicCardsComponent, DynamicCardSchemaItem } from '../components/dynamic-cards/dynamic-cards.component';
import { DefaultDropdown } from './form-fields.model';
import { DYNAMIC_IDENTIFIER } from './common.model';

export const surveyMetricsSchema: DynamicCardSchemaItem[] = [
  { label: 'Total Registrants', subLabel: 'Unique attendees', fieldKey: 'total_registrants', icon: 'fa-solid fa-users' },
  { label: 'Survey Completion Rate', subLabel: '', fieldKey: 'survey_completion_rate', suffix: '%', icon: 'fa-solid fa-circle-check' },
  { label: 'Responses Collected', subLabel: 'across all questions', fieldKey: 'responses_collected', icon: 'fa-solid fa-clipboard-list' },
];

export const surveyResponsesApiConfig: SearchConfig = {
  method: FETCH_METHODS['Get'],
  path: 'getPasSurveyResponses',
  urlPath: ApiEnvNames['EliteCertification']
};

export const dietaryBreakdownApiConfig: SearchConfig = {
  method: FETCH_METHODS['Get'],
  path: 'getPasDietaryBreakdown',
  urlPath: ApiEnvNames['EliteCertification'],
  body: {
    surveyId: ''
  }
};

export const responseRateByQuestionApiConfig: SearchConfig = {
  method: FETCH_METHODS['Get'],
  path: 'getPasResponseRateByQuestion',
  urlPath: ApiEnvNames['EliteCertification']
};

export const surveyMetricsApiConfig: SearchConfig = {
  method: FETCH_METHODS['Get'],
  path: 'pasSurveySummary',
  urlPath: ApiEnvNames['EliteCertification']
};

export const computeResponseRateByQuestion = (result: any[]): any[] =>
  result
    .map(item => ({
      ...item,
      responseCount: Number(item.responseCount),
      blankCount: Number(item.blankCount),
      totalResponses: Number(item.totalResponses),
      responseRate: Number(item.responseRate),
      responseDetails: item.responseDetails.filter((detail: any) => detail.answer),
    }))
    .sort((a, b) => b.responseRate - a.responseRate);

const responseRateExportConfig: exportConfig = {
  fileName: 'Response_Rate_By_Question',
  exportMessage: 'Response Rate by Question',
  schema: { question: 'Question', responseCount: 'Answered', blankCount: 'Not Answered', totalResponses: 'Total Responses', responseRate: 'Response Rate (%)' },
};

export const surveyResponseChart: CommonChartModel = {
  type: CHART_TYPE.ColumnChart,
  titleSchema: {
    label: '',
    title: 'Response Rate by Question',
    type: TITLE_TYPE.Static 
  },
  dataSchema: { 
    label: 'question',
    value: 'responseRate'
  },
  xAxis: { 
    type: 'category', 
    title: { text: 'Question' } 
  },
  yAxis: { 
    max: 100, 
    title: { text: 'Response Rate (%)' } 
  },
  series: [{ 
    name: 'Response rate', 
    color: '#238b91', 
    data: [] 
  }],
  exportConfig: responseRateExportConfig,
  plotOptions: { 
    series: { color: '#238b91', 
      dataLabels: { enabled: false } }, 
      column: { maxPointWidth: 40, pointPadding: 0.25, groupPadding: 0.2 } 
    },
  tooltip: {
    headerFormat: `<span style="font-size:11px">{point.name}</span><br>`,
    pointFormat: `<span>Answered</span>: <b>{point.dataset.responseCount}</b><br/><span>Not Answered</span>: <b>{point.dataset.blankCount}</b><br/>`
  }
};

const dietarySeries = [{ name: 'Dietary requirements', data: [] as any[], innerSize: '62%' }];

const dietaryExportConfig: exportConfig = {
  fileName: 'Dietary_Requirements_Breakdown',
  exportMessage: 'Dietary Requirements Breakdown',
  schema: { question: 'Question', answer: 'Answer', count: 'Count' },
  groupedSheets: {
    useResponseKeys: true,
    schemaMap: {
      answerBreakdown: {
        sheetName: 'Answer Summary',
        schema: { question: 'QUESTION', answer: 'ANSWER', count: 'COUNT' }
      },
      responseDetails: {
        sheetName: 'Response Details',
        schema: { question: 'QUESTION', spinId: 'SPIN ID', name: 'NAME', answer: 'ANSWER' }
      }
    }
  }
};

export const dietaryFilterSchema: DynamicFormArray = {
  FieldsArray: [{
    ...structuredClone(DefaultDropdown),
    fieldName: 'questionText',
    displayName: 'Select Dietary Requirements',
  }]
};

export const computeSurveyDietaryBreakdown = (result: any[]): any[] => {
  const rows = result
    .filter(item => Number(item.responseCount) > 0)
    .map(item => ({
      option: item.question,
      question: item.question,
      count: Number(item.responseCount),
      responseDetails: item.responseDetails
        .filter((detail: any) => detail.answer)
        .map((detail: any) => ({ ...detail, question: item.question })),
      answerBreakdown: item.answerSummary.map((entry: any) => ({ question: item.question, answer: entry.answer, count: Number(entry.count) })),
    }));
  const questionField = dietaryFilterSchema.FieldsArray[0];
  if (!questionField.options?.length) {
    questionField.options = rows.map(row => ({ fieldLabel: row.question, fieldValue: row.question }));
  }
  dietaryPendingAutoDrilldown = !!questionField.selectedValue;
  return rows;
};

const DIETARY_BASE_HEIGHT = 340;
const DIETARY_DRILLDOWN_MIN_HEIGHT = 250;
const DIETARY_DRILLDOWN_ROW_HEIGHT = 45;
const DIETARY_DRILLDOWN_HEIGHT_PADDING = 75;

const dietaryColorMap = new Map<string, string>();
let dietaryPendingAutoDrilldown = false;

const applyStableDietaryColors = (series: any): void => {
  const palette = (Highcharts.getOptions()?.colors ?? []) as string[];
  let hasChanges = false;
  series?.points?.forEach((point: any) => {
    const name = point?.name;
    if (!name) { return; }
    if (!dietaryColorMap.has(name)) {
      dietaryColorMap.set(name, palette[dietaryColorMap.size % (palette.length || 1)]);
    }
    const color = dietaryColorMap.get(name);
    if (color && point.color !== color) {
      point.update({ color }, false);
      hasChanges = true;
    }
  });
  if (hasChanges) {
    series.chart.redraw(false);
  }
};

const setDietaryBarMode = (chart: any, isDrilldown: boolean, answerCount = 0): void => {
  const height = isDrilldown
    ? Math.max(DIETARY_DRILLDOWN_MIN_HEIGHT, answerCount * DIETARY_DRILLDOWN_ROW_HEIGHT + DIETARY_DRILLDOWN_HEIGHT_PADDING)
    : DIETARY_BASE_HEIGHT;
  chart.update({
    chart: { inverted: isDrilldown, height },
    xAxis: { labels: { enabled: true }, lineWidth: 1, lineColor: '#ccd6eb' },
    yAxis: { gridLineWidth: 1, gridLineColor: '#e6e6e6' }
  }, true);
};

const bindDietaryDrilldownEvents = (chart: any): void => {
  if (chart.dietaryEventsBound) { return; }
  chart.dietaryEventsBound = true;
  Highcharts.addEvent(chart, 'drilldown', (event: any) => {
    if (event.seriesOptions && event.point) {
      event.seriesOptions.color = event.point.color;
      event.seriesOptions.colorByPoint = false;
    }
    setDietaryBarMode(chart, true, event.seriesOptions?.data?.length ?? 0);
  });
  Highcharts.addEvent(chart, 'drillup', () => {
    setTimeout(() => {
      if (!chart.drilldownLevels?.length) {
        setDietaryBarMode(chart, false);
      }
    });
  });
  Highcharts.addEvent(chart, 'redraw', () => autoOpenDietaryDrilldown(chart));
};

const autoOpenDietaryDrilldown = (chart: any): void => {
  if (!dietaryPendingAutoDrilldown || chart.drilldownLevels?.length) { return; }
  const drilldownPoint = chart.series?.[0]?.points?.find((point: any) => point.drilldown);
  if (!drilldownPoint) { return; }
  dietaryPendingAutoDrilldown = false;
  const container = chart.container;
  if (container) { container.style.visibility = 'hidden'; }
  const drilldownOptions = chart.options?.drilldown;
  const previousAnimation = drilldownOptions?.animation;
  if (drilldownOptions) { drilldownOptions.animation = false; }
  setTimeout(() => {
    drilldownPoint.doDrilldown?.();
    if (drilldownOptions) { drilldownOptions.animation = previousAnimation; }
    if (container) { container.style.visibility = ''; }
  });
};

function onDietaryPieRendered(this: any): void {
  const chart = this?.chart;
  if (!chart) { return; }
  applyStableDietaryColors(this);
  bindDietaryDrilldownEvents(chart);
  autoOpenDietaryDrilldown(chart);
}

export const surveyDietaryChart: CommonChartModel = { 
  type: CHART_TYPE.PieChart, 
  isDonut: true,
  titleSchema: { label: '', title: 'Dietary Requirements Breakdown', type: TITLE_TYPE.Static }, 
  drilldown: {
    breadcrumbs: { showFullPath: false, enabled: false },
    series: []
  },
  yAxis: { allowDecimals: false, min: 0 },
  dataSchema: { 
    label: 'option', 
    value: 'count',
    drillDownConfig: {
      drillDownField: 'answerBreakdown',
      type: CHART_TYPE.BarChart,
      label: 'answer',
      value: 'count',
      xAxis: { title: 'Answer' },
      yAxis: { title: 'Count', allowDecimals: false, min: 0 },
      dataLabels: { enabled: true, format: '{point.y}' },
      tooltip: {
        headerFormat: `<span style="font-size:11px">{point.name}</span><br>`,
        pointFormat: `<span>Count</span>: <b>{point.y}</b><br/>`
      },
      titleSchema: {
        label: '',
        title: `${DYNAMIC_IDENTIFIER['drilldownField']} Answer Breakdown`,
        subTitle: '',
        subLabel: '',
        type: TITLE_TYPE['Static']
      }
    }
  }, 
  series: dietarySeries,
  exportConfig: dietaryExportConfig,
  tooltip: { headerFormat: '', pointFormat: '<b>{point.name}</b>: {point.y}<br/>' },
  plotOptions: { 
    pie: { 
      innerSize: '62%',
      animation: false,
      events: { afterAnimate: onDietaryPieRendered },
      dataLabels: { 
        enabled: true, 
        format: '<b>{point.name}</b>: {point.y}',
        headerFormat: ''
      }
    }
  }
};

export const surveyGridSchema: dynamicComponentSchema[] = [
  {
    component: DynamicCardsComponent,
    type: SECTION_TYPE.Dashboard,
    inputs: {
      elementId: 'SurveyMetrics',
      schema: surveyMetricsSchema,
      apiConfig: surveyMetricsApiConfig,
      onLoadFetch: true,
      columns: 3,
      rows: 1,
      minCardWidth: '0',
      format: 'borderCard',
      showIcons: true
    }
  },
  {
    component: SurveyAttendeeTableComponent,
    type: SECTION_TYPE.List,
    inputs: {
      elementId: 'SurveyAttendees',
      apiConfig: surveyResponsesApiConfig,
      sectionTitle: 'All Attendee Responses'
    }
  },
  {
    component: ChartComponent,
    type: SECTION_TYPE['Chart'],
    inputs: {
      chartSchema: surveyDietaryChart,
      onLoadFetch: true,
      chartType: CHART_TYPE['PieChart'],
      elementId: 'survey-dietary-breakdown',
      containerClass: 'dashboard-chart-full survey-dietary-chart',
      apiConfig: dietaryBreakdownApiConfig,
      dataTransform: computeSurveyDietaryBreakdown,
      exportConfig: dietaryExportConfig,
      filterSchema: dietaryFilterSchema,
    },
  },
  {
    component: SurveyAnswerAccordionComponent,
    type: SECTION_TYPE.List,
    inputs: {
      apiConfig: surveyResponsesApiConfig,
      onLoadFetch: true
    }
  },
  {
    component: SurveyChartDrilldownComponent,
    type: SECTION_TYPE.Chart,
    inputs: {
      chartSchema: surveyResponseChart,
      chartType: CHART_TYPE.ColumnChart,
      elementId: 'survey-response-rate',
      containerClass: 'dashboard-chart-full',
      apiConfig: responseRateByQuestionApiConfig,
      dataTransform: computeResponseRateByQuestion,
      onLoadFetch: true,
      exportConfig: responseRateExportConfig,
      drilldownLabelKey: 'question',
      drilldownExportConfig: {
        fileName: 'Response_Rate_By_Question_Responses',
        exportMessage: 'Response Rate By Question',
        schema: { spinId: 'Spin ID', name: 'Name', answer: 'Answer' },
      },
    }
  },
];

export const getSurveyTabConfig = (): TabConfig[] => [{
  label: 'Survey',
  tabId: 'survey',
  type: SECTION_TYPE.Dashboard,
  component: DashboardContainerComponent,
  inputs: {
     gridSchema: surveyGridSchema 
    },
}];
